import {
  Injectable,
  PLATFORM_ID,
  inject,
  DestroyRef,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

const MODAL_SELECTOR = '.modal-backdrop, .modal-overlay';
const SCROLLABLE_MODAL_SELECTOR =
  '.modal-body, .audit-modal-body, .comparison-table-wrap, .audit-changes-panel, .ent-modal, .modal-container';

/**
 * Locks page scroll while modals are open (all viewports).
 * Watches DOM for modal-backdrop / modal-overlay so all list modals are covered without per-page wiring.
 */
@Injectable({ providedIn: 'root' })
export class ModalScrollLockService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  private observer: MutationObserver | null = null;
  private locked = false;
  private mainScrollTop = 0;
  private bodyScrollY = 0;
  private scrollbarPadding = 0;
  private touchMoveHandler: ((e: TouchEvent) => void) | null = null;
  private wheelHandler: ((e: WheelEvent) => void) | null = null;

  init(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.syncLockState();

    this.observer = new MutationObserver(() => {
      requestAnimationFrame(() => this.syncLockState());
    });
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    this.destroyRef.onDestroy(() => this.destroy());
  }

  private syncLockState(): void {
    const shouldLock = document.querySelectorAll(MODAL_SELECTOR).length > 0;

    if (shouldLock && !this.locked) {
      this.applyLock();
    } else if (!shouldLock && this.locked) {
      this.releaseLock();
    }
  }

  private applyLock(): void {
    if (this.locked) return;
    this.locked = true;

    const main = document.querySelector('.main-content') as HTMLElement | null;
    if (main) {
      this.mainScrollTop = main.scrollTop;
      main.classList.add('modal-scroll-locked');
    }

    this.bodyScrollY = window.scrollY;
    this.scrollbarPadding = Math.max(
      0,
      window.innerWidth - document.documentElement.clientWidth,
    );

    document.documentElement.classList.add('modal-scroll-locked');
    document.body.classList.add('modal-scroll-locked');

    if (this.scrollbarPadding > 0) {
      document.body.style.paddingRight = `${this.scrollbarPadding}px`;
    }

    document.body.style.top = `-${this.bodyScrollY}px`;
    document.body.style.width = '100%';

    this.touchMoveHandler = (e: TouchEvent) => this.onTouchMove(e);
    this.wheelHandler = (e: WheelEvent) => this.onWheel(e);
    document.addEventListener('touchmove', this.touchMoveHandler, {
      passive: false,
    });
    document.addEventListener('wheel', this.wheelHandler, { passive: false });
  }

  private releaseLock(): void {
    if (!this.locked) return;
    this.locked = false;

    const main = document.querySelector('.main-content') as HTMLElement | null;
    if (main) {
      main.classList.remove('modal-scroll-locked');
      main.scrollTop = this.mainScrollTop;
    }

    document.documentElement.classList.remove('modal-scroll-locked');
    document.body.classList.remove('modal-scroll-locked');
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.paddingRight = '';

    window.scrollTo(0, this.bodyScrollY);

    if (this.touchMoveHandler) {
      document.removeEventListener('touchmove', this.touchMoveHandler);
      this.touchMoveHandler = null;
    }
    if (this.wheelHandler) {
      document.removeEventListener('wheel', this.wheelHandler);
      this.wheelHandler = null;
    }
  }

  private onTouchMove(event: TouchEvent): void {
    if (!this.locked) return;
    const target = event.target;
    if (target instanceof Node && this.isWithinScrollableModal(target)) {
      return;
    }
    event.preventDefault();
  }

  private onWheel(event: WheelEvent): void {
    if (!this.locked) return;
    const target = event.target;
    if (target instanceof Node && this.isWithinScrollableModal(target)) {
      return;
    }
    event.preventDefault();
  }

  private isWithinScrollableModal(node: Node): boolean {
    if (!(node instanceof Element)) return false;

    const modalRoot = node.closest(MODAL_SELECTOR);
    if (!modalRoot) return false;

    const scrollable = node.closest(SCROLLABLE_MODAL_SELECTOR);
    if (!scrollable || !modalRoot.contains(scrollable)) return false;

    return scrollable.scrollHeight > scrollable.clientHeight + 1;
  }

  private destroy(): void {
    this.observer?.disconnect();
    this.observer = null;
    if (this.locked) {
      this.releaseLock();
    }
  }
}
